// ============================================================================
// achievementsTracking — desbloqueia conquistas no fechamento do mês
// ============================================================================
// Storage:
//   fk_achievements_v1 = { [profId]: { [categoryId]: { highestTier, history: [...] } } }
//   fk_achievement_notifications_v1 = [{ id, profId, categoryId, tier, value, monthKey, createdAt, readAt }]
//   fk_achievements_processed_v1 = { [monthKey]: ISO }   // evita reprocessar
//
// Avaliação roda apenas para meses JÁ FECHADOS (mês anterior ao corrente).
// Para cada profissional, comparamos o realizado do mês com os tiers da categoria
// e desbloqueamos qualquer tier ≤ valor que ainda não esteja desbloqueado.
// ============================================================================

import { ACHIEVEMENT_CATEGORIES, getTierRank, ACHIEVEMENTS_SCHEMA_VERSION, ACHIEVEMENT_VALIDITY_MONTHS } from '../data/achievements.js';
import { perfDoMes } from './perfPessoaMes.js';

const ACH_KEY = 'fk_achievements_v1';
const NOTIF_KEY = 'fk_achievement_notifications_v1';
const PROCESSED_KEY = 'fk_achievements_processed_v1';
const SCHEMA_KEY = 'fk_achievements_schema_version';

// Lê o valor numérico de uma métrica de um registro de performance, tratando
// os formatos NÃO-numéricos do merge:
//   • pacotesVendidos vem como OBJETO { [pkgId]: qtd } → soma os valores
//     (Number({...}) seria NaN→0, zerando conquistas de Recepcionista).
//   • faturamentoPacotes não existe no merge; o R$ de pacotes por prof se
//     chama valorPacotes → usa esse como fallback.
//   • qualquer outra métrica em objeto → soma; senão Number().
export function readMetricValue(rec, metric) {
  if (!rec) return 0;
  const raw = rec[metric];
  if (raw && typeof raw === 'object') {
    return Object.values(raw).reduce((s, x) => s + (Number(x) || 0), 0);
  }
  if ((metric === 'faturamentoPacotes') && (raw == null)) {
    return Number(rec.valorPacotes) || 0;
  }
  return Number(raw) || 0;
}

// Detecta dados com SCHEMA ANTIGO comparando tier values salvos com os atuais.
// Verificação direta — não depende de SCHEMA_KEY (que pode ter sido sobrescrito).
function hasOldSchemaData(achievementsData) {
  if (!achievementsData || typeof achievementsData !== 'object') return false;
  for (const profData of Object.values(achievementsData)) {
    if (!profData || typeof profData !== 'object') continue;
    for (const [catId, rec] of Object.entries(profData)) {
      if (!rec || !Array.isArray(rec.history)) continue;
      // Encontra qual role/categoria. Procura categoria com esse id em qualquer role.
      let cat = null;
      for (const cats of Object.values(ACHIEVEMENT_CATEGORIES)) {
        const found = cats.find(c => c.id === catId);
        if (found) { cat = found; break; }
      }
      if (!cat) continue;
      // Pra cada entry no history, valida se tierValue bate com o schema atual.
      for (const entry of rec.history) {
        if (typeof entry.tier !== 'number' || typeof entry.tierValue !== 'number') continue;
        const expectedValue = cat.tiers[entry.tier];
        if (expectedValue !== entry.tierValue) {
          // Tier value não bate → schema mudou → reset
          return true;
        }
      }
      // Verifica se highestTier > tiers.length atual (schema encolheu)
      if (rec.highestTier >= cat.tiers.length) return true;
    }
  }
  return false;
}

// Reset completo: local + cloud sync.
// Retorna { reset: bool, reason: string }
export async function ensureSchemaUpToDate(opts = {}) {
  const stored = parseInt(localStorage.getItem(SCHEMA_KEY) || '0', 10);
  const versionMismatch = stored < ACHIEVEMENTS_SCHEMA_VERSION;
  // Verifica direto nos dados se há schema antigo
  let hasOldData = false;
  try {
    const ach = JSON.parse(localStorage.getItem(ACH_KEY) || '{}');
    hasOldData = hasOldSchemaData(ach);
  } catch {}

  if (versionMismatch || hasOldData) {
    // Limpa local
    localStorage.removeItem(ACH_KEY);
    localStorage.removeItem(NOTIF_KEY);
    localStorage.removeItem(PROCESSED_KEY);
    localStorage.setItem(SCHEMA_KEY, String(ACHIEVEMENTS_SCHEMA_VERSION));
    // Limpa cloud também (objeto vazio sobrescreve a versão antiga)
    if (opts.cloudSave) {
      try {
        await opts.cloudSave(ACH_KEY, {});
        await opts.cloudSave(NOTIF_KEY, []);
      } catch {}
    }
    return { reset: true, reason: versionMismatch ? 'version' : 'tierValueMismatch' };
  }
  return { reset: false };
}

function read(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); }
  catch { return fallback; }
}
function write(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
}

export function getAchievements() { return read(ACH_KEY, {}); }
export function getNotifications() { return read(NOTIF_KEY, []); }
export function getProcessedMonths() { return read(PROCESSED_KEY, {}); }

export function getAchievementsForProfessional(profId) {
  const all = getAchievements();
  return all[profId] || {};
}

// Desbloqueia tiers para um profissional baseado nos valores do mês.
// monthKey: 'YYYY-MM' do mês fechado.
// metricsByProfId: { [profId]: { faturamento, comissao, ticketMedio, ... } }
function unlockTiersForProfessional(profId, role, metrics, monthKey) {
  const cats = ACHIEVEMENT_CATEGORIES[role];
  if (!cats) return [];
  const all = getAchievements();
  const newNotifs = [];
  if (!all[profId]) all[profId] = {};
  for (const cat of cats) {
    const value = Number(metrics?.[cat.metric] || 0);
    if (!isFinite(value) || value <= 0) continue;
    if (!all[profId][cat.id]) all[profId][cat.id] = { highestTier: -1, history: [] };
    const rec = all[profId][cat.id];
    // Maior tier alcançado pelo valor do mês
    let highestThisMonth = -1;
    for (let i = 0; i < cat.tiers.length; i++) {
      if (value >= cat.tiers[i]) highestThisMonth = i;
    }
    if (highestThisMonth < 0) continue;
    // Desbloqueia todos os tiers entre rec.highestTier + 1 e highestThisMonth
    if (highestThisMonth > rec.highestTier) {
      for (let i = rec.highestTier + 1; i <= highestThisMonth; i++) {
        const tierValue = cat.tiers[i];
        rec.history.push({ tier: i, tierValue, value, monthKey, unlockedAt: new Date().toISOString() });
        newNotifs.push({
          id: `notif-${profId}-${cat.id}-${i}-${monthKey}`,
          profId,
          categoryId: cat.id,
          categoryLabel: cat.label,
          role,
          tier: i,
          totalTiers: cat.tiers.length,
          tierValue,
          tierRank: getTierRank(i, cat.tiers.length),
          monthKey,
          createdAt: new Date().toISOString(),
          readAt: null,
        });
      }
      rec.highestTier = highestThisMonth;
    }
  }
  write(ACH_KEY, all);
  return newNotifs;
}

// Avalia o fechamento de um mês para todos os profissionais ativos.
// performance: array merged (já com Avec) — cada record tem professionalId, year, month, faturamento, etc.
// professionals: lista de profissionais ativos com role e id.
// Retorna a lista de notificações geradas.
export function evaluateMonth(monthKey, professionals, performance, opts = {}) {
  const processed = getProcessedMonths();
  // Mês corrente sempre é reavaliado (numbers mudam). Outros: skip se já processado.
  if (!opts.force && processed[monthKey]) return [];
  const [year, month] = monthKey.split('-').map(Number);
  const newNotifs = [];
  // Para cada profissional ativo cujo role tenha conquistas
  for (const pro of professionals) {
    if (pro.active === false) continue;
    if (!ACHIEVEMENT_CATEGORIES[pro.role]) continue;
    // Número da PESSOA (26/10/2026): com número em duas unidades no mês a
    // conquista olha a soma. Conquista de mês fechado fica, então a metade de
    // uma unidade nunca pode valer sozinha.
    const rec = perfDoMes(performance, pro, year, month);
    if (!rec) continue;
    // Para Recepcionista precisamos de "faturamentoPacotes" e "pacotesVendidos".
    // O merger expõe: pacotesVendidos. Já faturamento de pacotes não está agregado por prof
    // — usar pacotesVendidos como métrica disponível e R$ vai ficar pendente até backend ter.
    // Por ora, derivamos faturamentoPacotes a partir de pacotesVendidos*ticketMedio se ausente.
    const metrics = {
      faturamento: rec.faturamento || 0,
      comissao: rec.comissao || 0,
      ticketMedio: rec.ticketMedio || 0,
      agendamentosPreferenciais: rec.agendamentosPreferenciais || 0,
      produtosVendidos: rec.produtosVendidos || 0,
      // pacotesVendidos é objeto {pkgId:qtd} e faturamentoPacotes vira valorPacotes
      // no merge — normaliza pra número, senão Recepcionista nunca destrava tier.
      pacotesVendidos: readMetricValue(rec, 'pacotesVendidos'),
      faturamentoPacotes: readMetricValue(rec, 'faturamentoPacotes'),
      servicosRealizados: rec.servicosRealizados || 0,
    };
    const notifs = unlockTiersForProfessional(pro.id, pro.role, metrics, monthKey);
    newNotifs.push(...notifs);
  }
  // Persiste notificações
  if (newNotifs.length > 0) {
    const existing = getNotifications();
    write(NOTIF_KEY, [...existing, ...newNotifs]);
  }
  // Marca mês como processado
  processed[monthKey] = new Date().toISOString();
  write(PROCESSED_KEY, processed);
  return newNotifs;
}

// Avalia TODOS os meses ELEGÍVEIS — passados (fechados) + mês corrente APENAS
// no último dia do mês.
//
// Por que só no último dia: as conquistas usam métricas acumulativas (ticket
// médio, faturamento, comissão, qtd de serviços/produtos). Avaliar no meio do
// mês destravava tiers com base em dados parciais — ex: dia 1, 1 atendimento
// de R$ 200 já desbloqueava o tier Bronze de Ticket Médio. E como
// `unlockTiersForProfessional` SÓ SOBE (nunca regride), uma vez liberado
// ficaria grudado mesmo se o ticket médio do mês fechado fosse menor.
//
// Comportamento agora:
//   - Meses passados: processados 1× cada (idempotente via PROCESSED_KEY)
//   - Mês corrente: só processado se hoje for o ÚLTIMO DIA do mês
//   - Dia 1 do mês seguinte: o mês anterior já caiu pra "passado" e roda 1×
export function evaluateAllClosedMonths(professionals, performance, options = {}) {
  const now = new Date();
  const currentY = now.getFullYear();
  const currentM = now.getMonth() + 1;
  const currentMonthKey = `${currentY}-${String(currentM).padStart(2, '0')}`;
  // new Date(year, monthIdx1Based, 0) = último dia do mês anterior ao monthIdx1Based,
  // que é o último dia de currentM em 1-base. Ex: (2026, 5, 0) → 31/05/2026.
  const lastDayOfMonth = new Date(currentY, currentM, 0).getDate();
  const isLastDayOfMonth = now.getDate() === lastDayOfMonth;
  // Pega TODOS os meses presentes na performance (passados + corrente)
  const monthSet = new Set();
  for (const r of (performance || [])) {
    if (!r || typeof r.year !== 'number' || typeof r.month !== 'number') continue;
    monthSet.add(`${r.year}-${String(r.month).padStart(2, '0')}`);
  }
  const sorted = [...monthSet].sort();
  const allNew = [];
  for (const monthKey of sorted) {
    const isCurrent = monthKey === currentMonthKey;
    // Pula o mês corrente se ainda não chegou o último dia.
    // Os meses fechados rodam normal (idempotente).
    if (isCurrent && !isLastDayOfMonth) continue;
    // Mês corrente NO último dia: força re-avaliação (caso já tenha rodado mais cedo no dia)
    // Passados: idempotente (só na 1ª vez)
    const notifs = evaluateMonth(monthKey, professionals, performance, { force: isCurrent });
    allNew.push(...notifs);
  }
  return allNew;
}

// Notificações
export function markNotificationRead(id) {
  const list = getNotifications();
  const updated = list.map(n => n.id === id ? { ...n, readAt: new Date().toISOString() } : n);
  write(NOTIF_KEY, updated);
}
export function markAllNotificationsRead(profId) {
  const list = getNotifications();
  const now = new Date().toISOString();
  const updated = list.map(n => (n.profId === profId && !n.readAt) ? { ...n, readAt: now } : n);
  write(NOTIF_KEY, updated);
}
export function getNotificationsForProfessional(profId) {
  return getNotifications().filter(n => n.profId === profId);
}
export function getUnreadCount(profId) {
  return getNotifications().filter(n => n.profId === profId && !n.readAt).length;
}

// Categoria que determina a borda do avatar por cargo:
// - Barbeiro: Ticket Médio (atualizada conforme performance dos últimos 3 meses)
// - Recepcionista: Faturamento de Pacotes
// - Assistente: Faturamento
// - Outros: faturamento (default)
function getBorderCategoryId(role) {
  if (role === 'Barbeiro') return 'tkt';
  if (role === 'Recepcionista') return 'fatpac';
  return 'fat';
}

// Calcula a borda do avatar (rank) usando a janela de validade de 6 meses
// — mesma regra das conquistas. Se piorar e sair da janela, rebaixa.
//
// @param performance — array de records merged (já com Avec). REQUIRED.
// @param refDate — Date opcional, default = hoje.
export function getProfileBorderRank(profId, role, performance, refDate) {
  const cats = ACHIEVEMENT_CATEGORIES[role];
  if (!cats) return null;
  const cat = cats.find(c => c.id === getBorderCategoryId(role));
  if (!cat) return null;

  // Sem performance carregada: fallback pro storage antigo (modo legacy).
  if (!Array.isArray(performance)) {
    const profAch = getAchievementsForProfessional(profId);
    const rec = profAch[cat.id];
    if (!rec || rec.highestTier < 0) return null;
    return {
      rank: getTierRank(rec.highestTier, cat.tiers.length),
      tier: rec.highestTier,
      tierValue: cat.tiers[rec.highestTier],
      totalTiers: cat.tiers.length,
    };
  }

  // Usa a mesma engine de active tier do compute geral (janela 6m).
  const computed = computeAchievementsForProfessional(profId, role, performance, refDate);
  const rec = computed[cat.id];
  if (!rec || rec.activeTier < 0) return null;
  return {
    rank: getTierRank(rec.activeTier, cat.tiers.length),
    tier: rec.activeTier,
    tierValue: cat.tiers[rec.activeTier],
    totalTiers: cat.tiers.length,
    metric: cat.metric,
    maxValue: rec.activeValue,
    historicalTier: rec.historicalTier,
    hasLostTiers: rec.lostTiers.length > 0,
  };
}

// Helper para resetar (desenvolvimento/admin)
export function resetAchievements() {
  localStorage.removeItem(ACH_KEY);
  localStorage.removeItem(NOTIF_KEY);
  localStorage.removeItem(PROCESSED_KEY);
}

// ============================================================================
// computeAchievementsForProfessional — visão derivada das conquistas
// ============================================================================
// Conquistas têm validade de N meses (ACHIEVEMENT_VALIDITY_MONTHS = 6). Se o
// profissional não repetir o desempenho dentro dessa janela, a conquista
// vai pra "histórico". Esta função NÃO usa o storage — calcula tudo a partir
// da performance mensal. Storage continua existindo só pras notificações de
// destrava/regressão.
//
// Retorna por categoria:
//   {
//     activeTier: -1 | number          // tier máximo alcançado em meses fechados nos últimos N
//     activeValue: number              // valor que sustenta o activeTier
//     historicalTier: -1 | number      // tier máximo já alcançado algum dia
//     historicalValue: number          // valor que originou o historicalTier
//     lostTiers: [{                    // tiers que ele já teve mas não estão no ativo
//       tier, tierValue, lastSeenMonthKey, lostAtMonthKey
//     }],
//     monthlyMaxValue: { [monthKey]: number }  // pra debug/timeline
//   }
//
// Considera apenas meses FECHADOS — mês corrente só entra se for o último dia.
// ============================================================================
export function computeAchievementsForProfessional(profId, role, performance, refDate) {
  const cats = ACHIEVEMENT_CATEGORIES[role];
  if (!cats || !profId) return {};

  const now = refDate || new Date();
  const curY = now.getFullYear();
  const curM = now.getMonth() + 1;
  const lastDayOfMonth = new Date(curY, curM, 0).getDate();
  const isLastDayOfMonth = now.getDate() === lastDayOfMonth;

  // Filtra performance do prof — só meses fechados (ou corrente no último dia)
  const myRecords = (performance || [])
    .filter(r => r.professionalId === profId)
    .filter(r => {
      const isCurrent = r.year === curY && r.month === curM;
      if (isCurrent && !isLastDayOfMonth) return false;
      // ignora futuros
      return r.year < curY || (r.year === curY && r.month <= curM);
    });

  // Janela ativa: últimos N meses fechados (anchor = mês fechado mais recente)
  // Anchor = se hoje é último dia do mês corrente, anchor = currentY/currentM,
  // senão anchor = mês anterior.
  let anchorY = curY, anchorM = curM;
  if (!isLastDayOfMonth) {
    anchorM -= 1;
    if (anchorM === 0) { anchorM = 12; anchorY -= 1; }
  }
  // Janela = [anchor - (N-1) meses, anchor]
  const windowStart = new Date(anchorY, anchorM - 1 - (ACHIEVEMENT_VALIDITY_MONTHS - 1), 1);
  const windowEnd = new Date(anchorY, anchorM - 1, 1); // 1º dia do anchor (comparação por mês)
  function inWindow(year, month) {
    const d = new Date(year, month - 1, 1);
    return d >= windowStart && d <= windowEnd;
  }
  function monthKey(year, month) {
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  const result = {};
  for (const cat of cats) {
    // Mapa monthKey → max value da métrica naquele mês
    const monthlyMaxValue = {};
    for (const r of myRecords) {
      const v = readMetricValue(r, cat.metric);
      if (v <= 0) continue;
      const k = monthKey(r.year, r.month);
      if ((monthlyMaxValue[k] || 0) < v) monthlyMaxValue[k] = v;
    }
    // Active: max valor dos meses dentro da janela
    let activeMaxValue = 0;
    for (const r of myRecords) {
      if (!inWindow(r.year, r.month)) continue;
      const v = readMetricValue(r, cat.metric);
      if (v > activeMaxValue) activeMaxValue = v;
    }
    // Historical: max de TODOS os meses
    let historicalMaxValue = 0;
    for (const r of myRecords) {
      const v = readMetricValue(r, cat.metric);
      if (v > historicalMaxValue) historicalMaxValue = v;
    }
    // Tier idx pra cada
    const tierFor = (val) => {
      let idx = -1;
      for (let i = 0; i < cat.tiers.length; i++) {
        if (val >= cat.tiers[i]) idx = i;
      }
      return idx;
    };
    const activeTier = tierFor(activeMaxValue);
    const historicalTier = tierFor(historicalMaxValue);

    // Tiers perdidos: do activeTier+1 até historicalTier (inclusive)
    const lostTiers = [];
    if (historicalTier > activeTier) {
      for (let t = activeTier + 1; t <= historicalTier; t++) {
        const tierValue = cat.tiers[t];
        // Last month que o pro alcançou esse tier (ou maior)
        let lastSeenMonthKey = null;
        let lastSeenDate = null;
        for (const [k, v] of Object.entries(monthlyMaxValue)) {
          if (v >= tierValue) {
            const [y, m] = k.split('-').map(Number);
            const d = new Date(y, m - 1, 1);
            if (!lastSeenDate || d > lastSeenDate) {
              lastSeenDate = d;
              lastSeenMonthKey = k;
            }
          }
        }
        // lostAt = mês logo após sair da janela de N meses
        let lostAtMonthKey = null;
        if (lastSeenDate) {
          const lostAt = new Date(lastSeenDate);
          lostAt.setMonth(lostAt.getMonth() + ACHIEVEMENT_VALIDITY_MONTHS);
          lostAtMonthKey = monthKey(lostAt.getFullYear(), lostAt.getMonth() + 1);
        }
        lostTiers.push({ tier: t, tierValue, lastSeenMonthKey, lostAtMonthKey });
      }
    }

    result[cat.id] = {
      activeTier,
      activeValue: activeMaxValue,
      historicalTier,
      historicalValue: historicalMaxValue,
      lostTiers,
      monthlyMaxValue,
    };
  }
  return result;
}

// Versão "all pros" — útil pra ranking / dashboards.
// Retorna: { [profId]: { [categoryId]: { ... } } }
export function computeAchievementsForAll(professionals, performance, refDate) {
  const out = {};
  for (const pro of (professionals || [])) {
    if (pro.active === false) continue;
    if (!ACHIEVEMENT_CATEGORIES[pro.role]) continue;
    out[pro.id] = computeAchievementsForProfessional(pro.id, pro.role, performance, refDate);
  }
  return out;
}
